import { Link, useParams } from 'react-router-dom';
import { articles } from '@/mocks/articles';
import ArticleCard from '@/components/feature/ArticleCard';
import { VerifiedBadge, channelName, formatNum } from '@/components/feature/ArticleBits';
import { usePageMeta, useJsonLd } from '@/hooks/usePageMeta';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === Number(id));

  usePageMeta(
    {
      title: article ? `${article.title} | 51爆料` : '文章不存在 | 51爆料',
      desc: article ? article.excerpt : '未找到对应文章。',
    },
    article ? `/article/${article.id}` : '/'
  );
  useJsonLd('ld-article', article ? {
    '@context': 'https://schema.org',
    '@type': 'NewsArticle',
    headline: article.title,
    description: article.excerpt,
    image: [article.cover],
    datePublished: `2026-${article.date}.00:00:00+08:00`,
    author: [{ '@type': 'Person', name: article.author }],
    publisher: { '@type': 'Organization', name: '51爆料' },
    mainEntityOfPage: `https://00279.top/article/${article.id}`,
    inLanguage: 'zh-CN',
  } : null);

  if (!article) {
    return (
      <div className="mx-auto flex max-w-3xl flex-col items-center px-4 py-28 text-center">
        <i className="ri-file-warning-line text-5xl text-primary-400" aria-hidden="true"></i>
        <h1 className="mt-4 font-display text-2xl font-bold text-foreground-950">这条爆料不存在或已下架</h1>
        <p className="mt-2 text-sm text-foreground-600">可能是链接有误，或内容已按相关流程更新。</p>
        <Link to="/" className="mt-6 rounded-md bg-primary-600 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-700">
          返回首页
        </Link>
      </div>
    );
  }

  const related = articles.filter((a) => a.id !== article.id).slice(0, 3);

  return (
    <div className="bg-background-50 py-10 md:py-14">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        {/* 面包屑 */}
        <nav aria-label="面包屑" className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500">
          <Link to="/" className="flex items-center gap-1 hover:text-primary-600">
            <i className="ri-home-4-line" aria-hidden="true"></i>首页
          </Link>
          <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
          <Link to="/" className="hover:text-primary-600">{channelName[article.channel]}</Link>
          <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
          <span className="line-clamp-1 max-w-[200px] text-foreground-400">正文</span>
        </nav>

        <div className="mt-6 grid gap-10 lg:grid-cols-12">
          {/* 主内容 */}
          <div className="lg:col-span-8">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-sm bg-secondary-100 px-2 py-0.5 text-xs font-medium text-secondary-900">
                {channelName[article.channel]}
              </span>
              <VerifiedBadge status={article.verified} />
            </div>
            <h1 className="mt-4 font-display text-2xl font-black leading-snug text-foreground-950 md:text-4xl md:leading-tight">
              {article.title}
            </h1>
            <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 border-b border-background-200 pb-5 text-sm text-foreground-600">
              <span className="flex items-center gap-2">
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-accent-100 text-accent-700">
                  <i className="ri-user-3-line" aria-hidden="true"></i>
                </span>
                <span>
                  <span className="font-medium text-foreground-900">{article.author}</span>
                  <span className="text-xs text-foreground-500"> · {article.role}</span>
                </span>
              </span>
              <span className="flex items-center gap-1.5 whitespace-nowrap">
                <i className="ri-time-line" aria-hidden="true"></i>2026-{article.date}
              </span>
              <span className="flex items-center gap-1.5 whitespace-nowrap">
                <i className="ri-eye-line" aria-hidden="true"></i>
                {formatNum(article.views)} 阅读
              </span>
              <span className="flex items-center gap-1.5 whitespace-nowrap">
                <i className="ri-chat-3-line" aria-hidden="true"></i>
                {formatNum(article.comments)} 讨论
              </span>
            </div>

            {/* 封面 */}
            <div className="mt-6 overflow-hidden rounded-lg bg-background-200">
              <img src={article.cover} alt={article.title} className="h-auto w-full object-cover" />
            </div>

            {/* 摘要导语 */}
            <p className="mt-6 border-l-4 border-primary-500 bg-primary-50/60 py-3 pl-4 pr-3 text-[15px] leading-7 text-foreground-800">
              {article.excerpt}
            </p>

            {/* 正文 */}
            <div className="prose-baoliao mt-7">
              {article.body.map((block, i) => {
                if (block.type === 'h') {
                  return (
                    <h3 key={i} id={`sec-${i}`}>
                      {block.text}
                    </h3>
                  );
                }
                if (block.type === 'quote') {
                  return (
                    <blockquote key={i} className="my-6 rounded-md border border-accent-200 bg-accent-50/60 px-5 py-4 text-sm italic leading-7 text-foreground-700">
                      <span className="mr-1 text-accent-500">「</span>
                      {block.text}
                      <span className="ml-1 text-accent-500">」</span>
                    </blockquote>
                  );
                }
                if (block.type === 'ul') {
                  return (
                    <ul key={i} className="my-4 flex flex-col gap-2 text-[15px] text-foreground-800">
                      <li className="flex items-start gap-2.5">
                        <i className="ri-corner-down-right-line mt-1 text-primary-500" aria-hidden="true"></i>
                        <span>{block.text}</span>
                      </li>
                    </ul>
                  );
                }
                return (
                  <p key={i}>
                    {block.text}
                  </p>
                );
              })}
            </div>

            {/* 标签 */}
            <div className="mt-8 flex flex-wrap gap-2">
              {article.tags.map((t) => (
                <Link key={t} to="/" className="rounded-full bg-background-100 px-3 py-1 text-xs text-foreground-700 hover:bg-secondary-200 hover:text-secondary-900">
                  <i className="ri-price-tag-3-line mr-1 text-secondary-600" aria-hidden="true"></i>
                  {t}
                </Link>
              ))}
            </div>

            {/* 免责与说明 */}
            <div className="mt-8 flex items-start gap-3 rounded-lg border border-background-200 bg-background-100/60 p-5">
              <i className="ri-information-line mt-0.5 text-xl text-secondary-600" aria-hidden="true"></i>
              <div className="text-xs leading-6 text-foreground-600">
                <p className="font-medium text-foreground-800">内容说明</p>
                <p className="mt-1">
                  本文为匿名投稿信息整理，观点归属投稿人。平台已按其核实状态如实标注，所涉具体事实以相关方官方信息与书面文件为准，不构成法律或事实结论。若您认为内容存在不实或侵权，欢迎通过联系我们页面向编辑部反映。
                </p>
              </div>
            </div>
          </div>

          {/* 侧栏 */}
          <aside className="lg:col-span-4" aria-label="相关阅读与投稿">
            {/* 投稿引导 */}
            <div className="rounded-lg bg-primary-600 p-6 text-background-50">
              <h3 className="flex items-center gap-2 font-display text-lg font-bold">
                <i className="ri-megaphone-fill text-accent-300" aria-hidden="true"></i>
                有同类的内部消息？
              </h3>
              <p className="mt-2 text-sm leading-6 text-primary-100">
                你的线索可能帮助更多人避开坑。匿名提交，编辑部会分级核实并持续更新。
              </p>
              <Link
                to="/about#contact"
                className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-accent-500 px-4 py-2 text-xs font-semibold text-foreground-950 hover:bg-accent-400"
              >
                <i className="ri-lock-2-fill" aria-hidden="true"></i>
                匿名投稿
              </Link>
            </div>

            {/* 投稿人信息卡 */}
            <div className="mt-5 rounded-lg border border-background-200 bg-background-50 p-6">
              <h4 className="flex items-center gap-2 font-display text-base font-bold text-foreground-950">
                <i className="ri-shield-user-fill text-accent-600" aria-hidden="true"></i>
                本页投稿人
              </h4>
              <div className="mt-3 flex items-center gap-3">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-accent-100 text-accent-700">
                  <i className="ri-user-3-line text-xl" aria-hidden="true"></i>
                </span>
                <div>
                  <p className="font-medium text-foreground-900">{article.author}</p>
                  <p className="text-xs text-foreground-500">{article.role}</p>
                </div>
              </div>
              <p className="mt-3 text-xs leading-6 text-foreground-600">
                身份已按平台机制做匿名化处理。投稿人仅供展示其内容立场，不代表平台观点。
              </p>
            </div>

            {/* 栏目目录 */}
            <div className="mt-5 rounded-lg border border-background-200 bg-background-50 p-6">
              <h4 className="flex items-center gap-2 font-display text-base font-bold text-foreground-950">
                <i className="ri-list-unordered text-primary-600" aria-hidden="true"></i>
                本章节目录
              </h4>
              <ul className="mt-3 flex flex-col gap-1">
                {article.body
                  .filter((b) => b.type === 'h')
                  .map((b, i) => (
                    <li key={i}>
                      <a href={`#sec-${article.body.indexOf(b)}`} className="flex items-center gap-2 text-sm text-foreground-700 hover:text-primary-700">
                        <span className="h-1.5 w-1.5 rounded-full bg-secondary-400" aria-hidden="true"></span>
                        {b.text}
                      </a>
                    </li>
                  ))}
              </ul>
            </div>
          </aside>
        </div>

        {/* 相关推荐 */}
        <section className="mt-12 border-t border-background-200 pt-8" aria-label="相关推荐">
          <h2 className="flex items-center gap-2 font-display text-xl font-bold text-foreground-950">
            <i className="ri-arrow-left-right-line text-primary-600" aria-hidden="true"></i>
            你可能还想看
          </h2>
          <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {related.map((a) => (
              <ArticleCard key={a.id} article={a} layout="card" showExcerpt={false} />
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}